<template>
  <PmPollingIndicator
    :initial-loading-finished="initialLoadingFinished"
    :is-updating-from-external="isUpdatingCalendarCache"
    :user-is-idle="userIsIdle"
    :trigger-update="triggerUpdate"
  />
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, provide } from 'vue'
import { useStore } from 'vuex'
import { useApolloClient } from '@vue/apollo-composable'
import type {
  ApolloQueryResult,
  ObservableQuery,
  OperationVariables,
} from '@apollo/client'

import { EVENT } from '@/constants/events'

import eventHub from '@/eventHub'
import { throwFriendlyError } from '@/functional/friendlyErrors'
import {
  transformToAlternativeEndOfDay,
  formatToServerDateString,
} from '@/utilities/date'
import { pollingIndicatorKey } from '@/utilities/inject'

import PmPollingIndicator from '@/components/basics/PmPollingIndicator/PmPollingIndicator.vue'

import {
  CalendarCacheDocument,
  SidebarCacheDocument,
} from '../../../../generated/graphql'

export interface Props {
  initialLoadingFinished?: boolean
  isUpdatingCalendarCache?: boolean
  userIsIdle?: boolean
}

const props = withDefaults(defineProps<Props>(), {})

provide(pollingIndicatorKey, {
  update: update,
})

const store = useStore()
let abortController: AbortController | undefined
const { client: apolloClient } = useApolloClient()

const startDateAlternativeStartOfDay = computed(() => {
  return transformToAlternativeEndOfDay(store.state.persoplan.visibleStartDate)
})

/**
 * Trigger update by event
 */
const triggerUpdate = ref(0)

function updateByEvent() {
  triggerUpdate.value += 1
}

eventHub.$on(EVENT.CACHE_CALENDAR_UPDATE, updateByEvent)

onBeforeUnmount(() => {
  eventHub.$on(EVENT.CACHE_CALENDAR_UPDATE, updateByEvent)
})

function watchQueryToPromise<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: ObservableQuery<TData, TVariables>
): Promise<ApolloQueryResult<TData>> {
  return new Promise((resolve, reject) => {
    const subscription = query.subscribe({
      next: (result) => {
        // Only resolve when data comes from network (not just cache)
        if (!result.loading && result.data) {
          subscription.unsubscribe()
          resolve(result)
        }
      },
      error: (error) => {
        subscription.unsubscribe()
        reject(error)
      },
    })
  })
}

/**
 * For information about how to do cancellation of queries
 * @see https://evilmartians.com/chronicles/aborting-queries-and-mutations-in-react-apollo
 * There is another method, by using watchQuery, but I couldn't  get this wot work
 * @see https://github.com/apollographql/apollo-client/issues/4150#issuecomment-500127694
 */
async function update() {
  try {
    // Check if there's an existing abortController and cancel it in case
    if (abortController) {
      abortController.abort()
      // We need to wait for the previous promise chain to resolve, otherwise the next abortController will get resetted too early
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  } catch (error) {
    throw new Error('Error when trying to abort previous request')
  }

  /**
   * The `promiseExit` has the sole purpose of exiting the async/await flow in case an
   * update is going to get cancelled. There is no way of accessing the resolve method
   * of the apollo-query apparently
   */
  let promiseExitResolve
  const promiseExit = new Promise((resolve) => {
    promiseExitResolve = resolve
  })

  abortController = new window.AbortController()
  abortController.signal.addEventListener('abort', promiseExitResolve, {
    once: true,
  })

  try {
    const queryVariables = store.getters['queryVariables/calendar']

    const calendarQuery = apolloClient.watchQuery({
      query: CalendarCacheDocument,
      fetchPolicy: 'cache-and-network',
      variables: {
        ...queryVariables,
        startDateAlternativeStartOfDay: formatToServerDateString(
          startDateAlternativeStartOfDay.value
        ),
      },
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
    })

    const sidebarQuery = apolloClient.watchQuery({
      query: SidebarCacheDocument,
      fetchPolicy: 'cache-and-network',
      variables: queryVariables,
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
    })

    // Convert watchQuery observables to promises
    const promiseCalendar = watchQueryToPromise(calendarQuery)
    const promiseSidebar = watchQueryToPromise(sidebarQuery)

    // Collect updates in a single promise
    const promiseQueries = Promise.all([promiseCalendar, promiseSidebar])

    // Wait for either all updates to finish or the exit promise to resolve
    await Promise.race([promiseExit, promiseQueries])

    // Reset abortController
    abortController = undefined
  } catch (error) {
    throwFriendlyError(error)
  }
}
</script>
